/**
 * @spec docs/superpowers/specs/2026-10-13-wallet-network-awareness-design.md §6.1
 * @standard docs/standards/staking-ui-wallet-actions.md#decide
 * Chain-id parsing and the wrong-network error. Pure functions, no React. What each wallet kind
 * does for each action lives in lib/wallet-actions/decide.ts; prepareWallet raises
 * WrongNetworkError (as the cause of a WalletActionError) when it asks an external wallet to
 * switch.
 */
import { getChainDisplayName } from './chain-registry';

export interface WrongNetworkInfo {
  currentChainId: number;
  requiredChainId: number;
}

export class WrongNetworkError extends Error implements WrongNetworkInfo {
  readonly currentChainId: number;
  readonly requiredChainId: number;

  constructor(currentChainId: number, requiredChainId: number) {
    super(
      `Your wallet is on ${getChainDisplayName(currentChainId)}. Switch to ${getChainDisplayName(requiredChainId)} and try again.`
    );
    this.name = 'WrongNetworkError';
    this.currentChainId = currentChainId;
    this.requiredChainId = requiredChainId;
  }
}

/**
 * Parse an eth_chainId reply. Anything other than a short 0x-hex string is "unknown" (null):
 * test doubles and odd providers answer every method with the same string, and a garbage reply
 * must never read as a mismatch.
 */
export function parseChainIdHex(value: unknown): number | null {
  if (typeof value !== 'string' || !/^0x[0-9a-f]{1,13}$/i.test(value)) return null;
  const parsed = parseInt(value, 16);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

/** Parse Privy's CAIP-2 chain ID (e.g. "eip155:8453") to a numeric chain ID; null if missing/garbage */
export function parseCaip2ChainId(caip2: string | undefined): number | null {
  if (!caip2) return null;
  return parseInt(caip2.split(':')[1] ?? '0', 10) || null;
}
